import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Mail, CheckCircle2, Send, RefreshCw, Package, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import endlessStorageLogo from "@/assets/endless-storage-logo.png";

// Loading stage messages - fast timing matching actual AI performance (3-4s)
const LOADING_STAGES = [
  { progress: 0, message: "Preparing your photo...", duration: 500 },
  { progress: 15, message: "Uploading to our servers...", duration: 1000 },
  { progress: 35, message: "AI is analyzing your items...", duration: 2000 },
  { progress: 65, message: "Identifying everything in your box...", duration: 1500 },
  { progress: 85, message: "Creating your inventory...", duration: 1000 },
  { progress: 95, message: "Almost done...", duration: 500 },
];

interface Item {
  name: string;
  quantity: number;
  room: string;
  category?: string;
  notes?: string;
}

interface NavigationState {
  items?: Item[];
  photoUrl?: string;
  sessionId?: string;
  fromAnalysis?: boolean;
  analyzing?: boolean;
  imageFile?: File;
  boxNumber?: string;
  sourceCode?: string;
}

export default function BoxAnalyzerResults() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const navigationState = location.state as NavigationState | null;
  
  const [items, setItems] = useState<Item[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string>("");
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [actualSessionId, setActualSessionId] = useState<string | null>(sessionId || null);
  const [isSaving, setIsSaving] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Timer effect for elapsed time during analysis
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAnalyzing) {
      setElapsedTime(0);
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAnalyzing]);

  // Progress animation effect - tracks stage with ref for early completion
  const [analysisComplete, setAnalysisComplete] = useState(false);
  
  useEffect(() => {
    if (!isAnalyzing) return;
    
    setAnalysisComplete(false);
    let currentStage = 0;
    let timeoutId: NodeJS.Timeout;
    
    const runStages = () => {
      if (currentStage >= LOADING_STAGES.length) return;
      
      const stage = LOADING_STAGES[currentStage];
      setUploadProgress(stage.progress);
      setLoadingMessage(stage.message);
      
      currentStage++;
      if (currentStage < LOADING_STAGES.length) {
        timeoutId = setTimeout(runStages, stage.duration);
      }
    };
    
    runStages();
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isAnalyzing]);
  
  // Early completion handler - jump to 100% when AI finishes before animation
  useEffect(() => {
    if (analysisComplete && isAnalyzing) {
      setUploadProgress(100);
      setLoadingMessage("Done!");
    }
  }, [analysisComplete, isAnalyzing]);

  // Run analysis when we arrive with an image to analyze
  useEffect(() => {
    const runAnalysis = async () => {
      if (!navigationState?.analyzing || !navigationState.imageFile) {
        // Check for completed analysis data
        if (navigationState?.fromAnalysis && navigationState.items) {
          console.log('Using navigation state data');
          setItems(navigationState.items);
          setPhotoUrl(navigationState.photoUrl || '');
          setActualSessionId(navigationState.sessionId || sessionId || null);
          setIsLoading(false);
        } else {
          // No navigation state - session expired
          console.log('No navigation state - session expired');
          setSessionExpired(true);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(false);
      setIsAnalyzing(true);
      setUploadProgress(0);

      try {
        const file = navigationState.imageFile;
        
        // Capture attribution data
        const urlParams = new URLSearchParams(window.location.search);
        const attribution = {
          utm_source: urlParams.get('utm_source') || undefined,
          utm_medium: urlParams.get('utm_medium') || undefined,
          utm_campaign: urlParams.get('utm_campaign') || undefined,
          utm_term: urlParams.get('utm_term') || undefined,
          utm_content: urlParams.get('utm_content') || undefined,
          referrer_url: document.referrer || undefined,
          landing_page: window.location.href,
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        // Convert to base64
        const reader = new FileReader();
        reader.readAsDataURL(file);
        
        reader.onload = async () => {
          try {
            const base64Image = reader.result as string;

            console.time('analyzeInvoke');

            // 60s client-side timeout fallback
            const timeoutId = setTimeout(() => {
              console.warn('[CLIENT] 60s timeout reached');
              setIsAnalyzing(false);
              setUploadProgress(0);
              toast({
                title: "Still Processing",
                description: "Analysis is taking longer than usual. Please try again with a clearer photo.",
                variant: "destructive",
              });
              navigate('/try');
            }, 60000);

            // Call edge function
            const { data, error } = await supabase.functions.invoke('analyze-box-anonymous', {
              body: { 
                imageData: base64Image,
                attribution,
                boxNumber: navigationState.boxNumber || undefined,
                sourceCode: navigationState.sourceCode
              }
            });

            clearTimeout(timeoutId);
            console.timeEnd('analyzeInvoke');
            console.log('Analyze response', data);

            if (error) {
              throw new Error(error.message || 'Failed to analyze image');
            }

            // Support both sessionId (camelCase) and session_id (snake_case)
            const newSessionId = (data as any)?.sessionId ?? (data as any)?.session_id;
            
            if (!newSessionId) {
              console.error('No session ID in response:', data);
              throw new Error('No session ID returned from analysis');
            }

            // Signal early completion to jump progress to 100%
            setAnalysisComplete(true);
            setActualSessionId(newSessionId);
            setItems(data.items || []);
            setPhotoUrl(data.photoUrl || data.photo_url || '');
            
            // Small delay to show 100% before hiding loader
            setTimeout(() => {
              setIsAnalyzing(false);
            }, 300);
            
            // Update URL to reflect the real session ID
            window.history.replaceState(null, '', `/try/results/${newSessionId}`);
          } catch (error: any) {
            console.error('Analysis error in reader:', error);
            setIsAnalyzing(false);
            setUploadProgress(0);
            toast({
              title: "Analysis Failed",
              description: error.message || "Failed to analyze your box. Please try again.",
              variant: "destructive",
            });
            navigate('/try');
          }
        };

        reader.onerror = () => {
          console.error('File reader error');
          setIsAnalyzing(false);
          setUploadProgress(0);
          toast({
            title: "File Error",
            description: "Failed to read image file. Please try again.",
            variant: "destructive",
          });
          navigate('/try');
        };

      } catch (error: any) {
        console.error('Analysis error:', error);
        toast({
          title: "Analysis Failed",
          description: error.message || "Failed to analyze your box. Please try again.",
          variant: "destructive",
        });
        setIsAnalyzing(false);
        navigate('/try');
      }
    };

    runAnalysis();
  }, [navigationState, sessionId, navigate, toast]);

  const handleSkipAnalysis = () => {
    setIsAnalyzing(false);
    setUploadProgress(0);
    toast({
      title: "Analysis Cancelled",
      description: "You can try again with a different photo.",
    });
    navigate('/try');
  };

  const handleSaveToAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    // Validate password if using password mode
    if (usePassword && passwordInput.length < 6) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters.",
        variant: "destructive",
      });
      return;
    }

    if (!items.length) {
      toast({
        title: "No Items",
        description: "No items to save. Please scan your box again.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      console.log('[BoxAnalyzerResults] Creating box from scan', { 
        email: emailInput.trim(),
        itemCount: items.length,
        sessionId: actualSessionId,
        usePassword,
      });
      
      // Create box immediately with optional password
      const { data, error } = await supabase.functions.invoke('create-box-from-scan', {
        body: { 
          items,
          photoUrl: photoUrl,
          email: emailInput.trim(),
          password: usePassword ? passwordInput : undefined,
          sourceCode: navigationState?.sourceCode,
          sessionId: actualSessionId,
          skipMagicLink: usePassword, // Skip magic link if using password
          boxNumber: navigationState?.boxNumber, // Pass partner label code if present
        }
      });

      if (error) {
        console.error('[BoxAnalyzerResults] Create box error:', error);
        throw new Error(error.message || 'Failed to save box');
      }

      if (data?.error) {
        console.error('[BoxAnalyzerResults] Create box returned error:', data.error);
        throw new Error(data.error);
      }

      console.log('[BoxAnalyzerResults] Box created successfully', data);

      // If using password, sign in immediately
      if (usePassword) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: emailInput.trim(),
          password: passwordInput,
        });

        if (signInError) {
          console.error('[BoxAnalyzerResults] Sign in error:', signInError);
          // Still show success - user can login manually
          toast({
            title: "Account Created!",
            description: "Your box is saved. You can now sign in with your email and password.",
          });
          setAccountCreated(true);
        } else {
          toast({
            title: "Welcome! ✨",
            description: `Your box with ${items.length} items has been saved.`,
          });
          // Navigate to dashboard
          navigate('/dashboard');
        }
      } else {
        // Magic link flow
        setAccountCreated(true);
        toast({
          title: "Box Saved! ✨",
          description: `Your box with ${items.length} items has been saved. Check your email to access your account.`,
        });
      }
    } catch (error: any) {
      console.error('[BoxAnalyzerResults] Save error:', error);
      toast({
        title: "Something went wrong",
        description: error.message || "Failed to save your box. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="h-12 w-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your results...</p>
        </div>
      </div>
    );
  }

  // Show analyzing state with progress bar
  if (isAnalyzing) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <header className="border-b bg-background/95 backdrop-blur">
          <div className="container mx-auto px-4 py-4">
            <a href="https://endless-storage.com" className="hover:opacity-80 transition-opacity">
              <img src={endlessStorageLogo} alt="Endless Storage" className="h-10" />
            </a>
          </div>
        </header>
        
        <div className="container mx-auto px-4 py-24">
          <div className="max-w-xl mx-auto">
            <Card className="p-12 border-2 border-primary/20">
              <div className="space-y-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Package className="h-8 w-8 text-primary animate-pulse" />
                </div>
                
                <h3 className="text-xl font-semibold">{loadingMessage || "Analyzing your box..."}</h3>
                
                <div className="space-y-2">
                  <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-500 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {elapsedTime}s elapsed
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">
                    💡 <strong>Tip:</strong> Our AI is identifying every item in your photo and categorizing them by room
                  </p>
                </div>

                {elapsedTime >= 10 && (
                  <Button 
                    variant="ghost" 
                    onClick={handleSkipAnalysis}
                    className="text-muted-foreground"
                  >
                    Taking too long? Try again
                  </Button>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show session expired state for page refresh/direct URL access
  if (sessionExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <header className="border-b bg-background/95 backdrop-blur">
          <div className="container mx-auto px-4 py-4">
            <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
              <img src={endlessStorageLogo} alt="Endless Storage" className="h-10" />
            </button>
          </div>
        </header>
        
        <div className="container mx-auto px-4 py-24">
          <div className="max-w-md mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6">
              <RefreshCw className="h-8 w-8 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Session Expired</h1>
            <p className="text-muted-foreground mb-8">
              This analysis session has expired. Scan a new box to see your results.
            </p>
            <Button onClick={() => navigate('/try')} size="lg">
              Scan Another Box
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 overflow-x-hidden">
      <header className="border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 py-4">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <img src={endlessStorageLogo} alt="Endless Storage" className="h-10" />
          </button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-green-600 mb-3 sm:mb-4">
              <Sparkles className="h-5 w-5 sm:h-6 sm:w-6" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">✨ We found {items.length} items in your box!</h1>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">Here's everything we detected</p>
          </div>

          {/* Results Grid */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-12 overflow-hidden">
              {/* Photo Column */}
              <div className="overflow-hidden w-full">
                <Card className="p-3 sm:p-4 overflow-hidden w-full">
                {photoUrl && (
                  <img 
                    src={photoUrl} 
                    alt="Your box" 
                    className="w-full h-auto rounded-lg"
                  />
                )}
                <p className="text-xs sm:text-sm text-muted-foreground text-center mt-2 px-2">Your uploaded photo</p>
              </Card>
            </div>

              {/* Items Column */}
              <div className="overflow-hidden w-full">
                <Card className="p-4 sm:p-6 overflow-hidden w-full">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h2 className="text-lg sm:text-xl font-semibold">Detected Items</h2>
                  <Badge variant="secondary" className="text-xs sm:text-sm">{items.length} items</Badge>
                </div>

                {items.length === 0 ? (
                  <div className="text-center py-6 sm:py-8 text-muted-foreground">
                    <p className="mb-2 text-sm sm:text-base">We didn't detect items in this photo.</p>
                    <p className="text-xs sm:text-sm">You can still save this box and add items manually later.</p>
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3 max-h-64 sm:max-h-96 overflow-y-auto">
                    {items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/50">
                        <div className="text-xl sm:text-2xl">📦</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-sm sm:text-base">{item.name}</p>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {item.room} {item.category && `• ${item.category}`}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">× {item.quantity}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>

          {/* Save to Account CTA */}
          <Card className="p-4 sm:p-6 md:p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <div className="max-w-xl mx-auto">
              {accountCreated ? (
                // Success state
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-2">
                    {usePassword ? "Account Created!" : "Check Your Email!"}
                  </h2>
                  <p className="text-sm sm:text-base text-muted-foreground mb-4">
                    {usePassword 
                      ? "Your box has been saved. You can now sign in with your email and password."
                      : <>We sent a magic link to <strong>{emailInput}</strong></>
                    }
                  </p>
                  {usePassword ? (
                    <Button onClick={() => navigate('/auth')}>
                      Go to Sign In
                    </Button>
                  ) : (
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Click the link in your email to access your account.
                    </p>
                  )}
                </div>
              ) : (
                // Email/Password input form
                <>
                  <div className="text-center mb-4 sm:mb-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 mb-3 sm:mb-4">
                      <Mail className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold mb-2">Save Your Box</h2>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      {usePassword 
                        ? "Create an account to save your inventory"
                        : "Enter your email and we'll send you a magic link"
                      }
                    </p>
                  </div>

                  <form onSubmit={handleSaveToAccount} className="space-y-4">
                    <div className="space-y-3">
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        required
                        disabled={isSaving}
                      />
                      
                      {usePassword && (
                        <Input
                          id="password"
                          type="password"
                          placeholder="Create a password (min 6 characters)"
                          value={passwordInput}
                          onChange={(e) => setPasswordInput(e.target.value)}
                          required
                          disabled={isSaving}
                          minLength={6}
                        />
                      )}
                      
                      <Button type="submit" className="w-full" disabled={isSaving}>
                        {isSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : usePassword ? (
                          <>
                            <Package className="h-4 w-4 mr-2" />
                            Create Account & Save Box
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Send Magic Link
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <div className="text-center">
                      <button
                        type="button"
                        className="text-sm text-primary hover:underline"
                        onClick={() => setUsePassword(!usePassword)}
                      >
                        {usePassword 
                          ? "Use magic link instead (no password needed)"
                          : "Use password instead (faster)"
                        }
                      </button>
                    </div>
                  </form>

                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-border/50">
                    <div className="grid gap-2 text-xs sm:text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        <span>Keep your inventory forever</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        <span>Create unlimited additional boxes</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        <span>Search all your items instantly</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Scan Another Box */}
          <div className="text-center mt-8">
            <Button variant="outline" onClick={() => navigate('/try')}>
              Scan Another Box
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}